// Ιδιαίτερες προτιμήσεις προπαραγγελίας (11/2026).
// - PrefsToggle: compact κουμπί expand/collapse γραμμής (βελάκι + μετρητής + ένδειξη σχολίου)
// - PrefsPicker: chips (από τον κατάλογο του Settings) + ελεύθερο σχόλιο
// - OrderPrefsPanel: πτυσσόμενη κάρτα «προτιμήσεις όλης της παραγγελίας»
// - NotesBubble: «συννεφάκι» στο BBQ με σχόλια πελάτη/κράτησης/προπαραγγελίας + προτιμήσεις
// Οι προτιμήσεις αποθηκεύονται ως ΟΝΟΜΑΤΑ (JSON λίστα), ώστε να διαβάζονται ως έχουν παντού.
import React from 'react';
import i18n from '../../i18n';
import './PreorderNotes.css';

const tr = i18n.t.bind(i18n);

export const parsePrefs = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (!value) return [];
  try {
    const v = JSON.parse(value);
    return Array.isArray(v) ? v.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
};

// ---- εικονίδια (inline SVG, παίρνουν το currentColor) ----
const svg = (path, cls = 'pn-ico') => (
  <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{path}</svg>
);
export const ChevronIcon = ({ open }) => svg(<path d="M9 6l6 6-6 6" />, `pn-chevron${open ? ' is-open' : ''}`);
export const CommentIcon = () => svg(<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />);
const CheckIcon = () => svg(<path d="M5 12.5l4.5 4.5L19 7.5" />);
const PlusIcon = () => svg(<path d="M12 5v14M5 12h14" />);
const AlertIcon = () => svg(<><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9L2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>);
const TagsIcon = () => svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><path d="M7.5 7.5h.01" /></>);

// Compact κουμπί για τις γραμμές: χωρίς κείμενο, μόνο βελάκι + μετρητής + 💬
export const PrefsToggle = ({ open, count = 0, hasComment, onClick }) => {
  const active = count > 0 || hasComment;
  return (
    <button type="button" onClick={onClick} aria-expanded={!!open}
      aria-label={tr('Preferences')} title={tr('Preferences')}
      className={`pn-toggle${active ? ' is-active' : ''}${open ? ' is-open' : ''}`}>
      <ChevronIcon open={open} />
      {count > 0 && <span className="pn-badge">{count}</span>}
      {hasComment && <CommentIcon />}
      {!active && <span className="pn-dash">—</span>}
    </button>
  );
};

// prefs: λίστα επιλεγμένων ονομάτων · catalog: [{id,name}] · onChange({prefs, comment})
export const PrefsPicker = ({ catalog = [], prefs = [], comment = '', onChange, commentPlaceholder }) => {
  const names = catalog.map((c) => c.name);
  // Επιλογές που υπάρχουν στην εγγραφή αλλά όχι πια στον κατάλογο: μένουν ορατές (να μη χαθούν σιωπηλά)
  const orphans = prefs.filter((p) => !names.includes(p));
  const toggle = (name) => {
    const next = prefs.includes(name) ? prefs.filter((p) => p !== name) : [...prefs, name];
    onChange({ prefs: next, comment });
  };
  return (
    <div className="pn-picker">
      <div className="pn-chips">
        {[...names, ...orphans].map((name) => {
          const on = prefs.includes(name);
          return (
            <button key={name} type="button" role="checkbox" aria-checked={on}
              className={`pn-chip${on ? ' is-on' : ''}`} onClick={() => toggle(name)}>
              {on ? <CheckIcon /> : <PlusIcon />} {name}
            </button>
          );
        })}
        {!names.length && !orphans.length && (
          <span className="pn-empty">{tr('No preferences configured (Settings → Kitchen & BBQ).')}</span>
        )}
      </div>
      <div className="pn-input-wrap">
        <CommentIcon />
        <input type="text" className="pn-input" value={comment} maxLength={255}
          placeholder={commentPlaceholder || tr('Comment')}
          onChange={(e) => onChange({ prefs, comment: e.target.value })} />
      </div>
    </div>
  );
};

// Κάρτα «όλη η παραγγελία»: κεφαλίδα (τίτλος + σύνοψη) που ανοιγοκλείνει
export const OrderPrefsPanel = ({ open, onToggle, catalog, value, onChange, title, hint, commentPlaceholder }) => {
  const prefs = value?.prefs || [];
  const comment = String(value?.comment || '');
  const summary = [...prefs, comment.trim()].filter(Boolean).join(' · ');
  return (
    <div className={`pn-order${open ? ' is-open' : ''}`}>
      <button type="button" className="pn-order-head" onClick={onToggle} aria-expanded={!!open}>
        <span className="pn-order-icon"><TagsIcon /></span>
        <span className="pn-order-text">
          <span className="pn-order-title">{title || tr('Whole order preferences')}</span>
          <span className="pn-order-sub">{summary || hint || tr('Preferences and comment that apply to the whole order')}</span>
        </span>
        {prefs.length > 0 && <span className="pn-badge">{prefs.length}</span>}
        {comment.trim() && <span style={{ color: '#b45309', display: 'inline-flex' }}><CommentIcon /></span>}
        <ChevronIcon open={open} />
      </button>
      {open && (
        <div className="pn-order-body">
          <PrefsPicker catalog={catalog} prefs={prefs} comment={comment}
            onChange={onChange} commentPlaceholder={commentPlaceholder || tr('Pre-order comment')} />
        </div>
      )}
    </div>
  );
};

// ---- BBQ: συννεφάκι σημειώσεων ----
// lines: [{icon, label, text}] · prefs: λίστα ονομάτων · collapsed/onToggle: ελεγχόμενο από τον γονέα
export const NotesBubble = ({ lines = [], prefs = [], collapsed, onToggle }) => {
  const visible = lines.filter((l) => String(l.text || '').trim());
  if (!visible.length && !prefs.length) return null;
  if (collapsed) {
    return (
      <button type="button" className="pn-pill" title={tr('Show notes')}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        <CommentIcon /> {visible.length + prefs.length}
      </button>
    );
  }
  return (
    <div className="pn-bubble" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="pn-bubble-close" title={tr('Hide notes')} aria-label={tr('Hide notes')}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        <ChevronIcon />
      </button>
      {!!prefs.length && (
        <div className="pn-tags">
          {prefs.map((p) => <span key={p} className="pn-tag"><AlertIcon /> {p}</span>)}
        </div>
      )}
      {visible.map((l, i) => (
        <div key={i} className="pn-line">
          <span aria-hidden="true">{l.icon}</span>
          <span className="pn-line-label">{l.label}</span>
          <span>{l.text}</span>
        </div>
      ))}
    </div>
  );
};
